import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, imgUrl } from "@/lib/api";
import { DEFAULT_THEME, applyTheme, useReloadSettings } from "@/lib/settings";
import { Card } from "./BookingsTab";
import { Field } from "./ListingTab";

const COLORS = [["ink", "Texto principal y fondos oscuros"], ["accent", "Acento (detalles, iconos)"], ["cta", "Botones de reservar"], ["forest", "Sección de reseñas"], ["paper", "Fondo claro"], ["paper2", "Fondo secundario (franja)"], ["muted", "Texto secundario"], ["body", "Texto de párrafos"], ["gold", "Detalles dorados sobre oscuro"], ["brown", "Subtítulos pequeños"], ["deep", "Fondo de la portada"]];
const DISPLAY = ["Cormorant Garamond", "Playfair Display", "Fraunces", "DM Serif Display", "Bodoni Moda", "Italiana", "Marcellus", "Libre Caslon Display", "Josefin Sans", "Montserrat"];
const BODY = ["Plus Jakarta Sans", "DM Sans", "Manrope", "Lato", "Nunito Sans", "Work Sans", "Karla", "Outfit", "Jost"];

const FontSelect = ({ value, options, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="bg-white"><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
  </Select>
);

const Scale = ({ label, value, min, max, step, unit, onChange, testid }) => (
  <Field label={`${label}: ${unit === "%" ? Math.round(value * 100) + "%" : value + unit}`}>
    <Slider data-testid={testid} min={unit === "%" ? min * 100 : min} max={unit === "%" ? max * 100 : max} step={unit === "%" ? step * 100 : step}
      value={[unit === "%" ? Math.round(value * 100) : value]} onValueChange={([v]) => onChange(unit === "%" ? v / 100 : v)} />
  </Field>
);

export const DesignTab = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const reload = useReloadSettings();
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS({ ...r.data, theme: { ...DEFAULT_THEME, ...(r.data.theme || {}), colors: { ...DEFAULT_THEME.colors, ...(r.data.theme?.colors || {}) } }, logo: r.data.logo || {} }));
  }, []);
  useEffect(() => {
    if (s) applyTheme(s.theme, s.logo);
  }, [s]);
  if (!s) return null;
  const th = s.theme;
  const setT = (k, v) => setS({ ...s, theme: { ...th, [k]: v } });
  const setC = (k, v) => setS({ ...s, theme: { ...th, colors: { ...th.colors, [k]: v } } });
  const setL = (k, v) => setS({ ...s, logo: { ...s.logo, [k]: v } });
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-media", fd);
      setL("url", data.url);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    try {
      await api.put("/admin/settings", { theme: th, logo: s.logo });
      reload();
      toast.success("Diseño guardado y publicado");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-12" data-testid="admin-design">
      <div className="space-y-6 lg:col-span-7">
        <Card className="space-y-4">
          <p className="font-display text-2xl">Logo</p>
          <div className="flex flex-wrap items-center gap-4 rounded-xl bg-[#2C2016] p-4">
            {s.logo.url ? <img src={imgUrl(s.logo.url)} alt="logo" style={{ height: Number(s.logo.height) || 36 }} /> : <span className="text-sm text-white/80">Logo por defecto</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" hidden accept="image/png,image/webp,image/jpeg" onChange={(e) => upload(e.target.files?.[0])} />
            <button data-testid="logo-upload-button" disabled={busy} onClick={() => fileRef.current?.click()} className="flex items-center gap-2 rounded-lg bg-[#2C2016] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Upload className="h-4 w-4" />{busy ? "Subiendo…" : "Subir logo (PNG transparente)"}</button>
            {s.logo.url && <button data-testid="logo-remove-button" onClick={() => setL("url", "")} className="rounded-lg border border-[#2C2016]/20 px-4 py-2 text-sm">Usar logo por defecto</button>}
          </div>
          <Scale testid="logo-height-slider" label="Altura del logo" value={Number(s.logo.height) || 36} min={20} max={90} step={2} unit="px" onChange={(v) => setL("height", v)} />
          <label className="flex items-center justify-between text-sm">Mostrar el nombre junto al logo <Switch data-testid="logo-text-switch" checked={s.logo.show_text !== false} onCheckedChange={(v) => setL("show_text", v)} /></label>
          <p className="text-xs text-[#4A3525]">El logo también se usa como icono de la pestaña del navegador.</p>
        </Card>
        <Card className="space-y-4">
          <p className="font-display text-2xl">Colores</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {COLORS.map(([k, l]) => (
              <label key={k} className="flex items-center gap-3 rounded-xl border border-[#2C2016]/10 p-2">
                <input data-testid={`color-${k}`} type="color" value={th.colors[k]} onChange={(e) => setC(k, e.target.value)} className="h-10 w-12 cursor-pointer rounded border-0 bg-transparent" />
                <span className="text-sm"><span className="block font-medium">{l}</span><Input value={th.colors[k]} onChange={(e) => setC(k, e.target.value)} className="mt-1 h-7 w-28 text-xs" /></span>
              </label>
            ))}
          </div>
        </Card>
      </div>
      <div className="space-y-6 lg:col-span-5">
        <Card className="space-y-4">
          <p className="font-display text-2xl">Tipografía</p>
          <Field label="Fuente de títulos"><FontSelect testid="font-display-select" value={th.font_display} options={DISPLAY} onChange={(v) => setT("font_display", v)} /></Field>
          <p className="font-display text-3xl">Samara Beach House</p>
          <Field label="Fuente de textos"><FontSelect testid="font-body-select" value={th.font_body} options={BODY} onChange={(v) => setT("font_body", v)} /></Field>
          <p className="text-sm">Casa de teca a 50 metros de la playa.</p>
        </Card>
        <Card className="space-y-5">
          <p className="font-display text-2xl">Tamaños de texto</p>
          <Scale testid="hero-size-slider" label="Título de portada" value={th.hero_size} min={48} max={160} step={2} unit="px" onChange={(v) => setT("hero_size", v)} />
          <Scale testid="heading-scale-slider" label="Títulos de secciones" value={th.heading_scale} min={0.7} max={1.5} step={0.05} unit="%" onChange={(v) => setT("heading_scale", v)} />
          <Scale testid="body-scale-slider" label="Textos y párrafos" value={th.body_scale} min={0.8} max={1.4} step={0.05} unit="%" onChange={(v) => setT("body_scale", v)} />
        </Card>
        <Card className="space-y-5">
          <p className="font-display text-2xl">Texto sobre la portada</p>
          <label className="flex items-center justify-between gap-3 text-sm">Color del texto <input data-testid="hero-text-color" type="color" value={th.hero_text_color || "#FBF8F2"} onChange={(e) => setT("hero_text_color", e.target.value)} className="h-9 w-12 cursor-pointer" /></label>
          <label className="flex items-center justify-between text-sm">Título en MAYÚSCULAS <Switch data-testid="title-upper-switch" checked={th.title_upper !== false} onCheckedChange={(v) => setT("title_upper", v)} /></label>
          <p className="pt-2 text-xs font-semibold uppercase tracking-wider text-[#4A3525]">Línea pequeña superior</p>
          <Scale testid="overline-size-slider" label="Tamaño" value={Number(th.overline_size) || 12} min={9} max={40} step={1} unit="px" onChange={(v) => setT("overline_size", v)} />
          <Scale testid="overline-spacing-slider" label="Espaciado entre letras" value={th.overline_spacing ?? 0.38} min={0} max={0.6} step={0.02} unit="em" onChange={(v) => setT("overline_spacing", Math.round(v * 100) / 100)} />
          <Field label="Estilo de letra">
            <Select value={th.overline_font || "mono"} onValueChange={(v) => setT("overline_font", v)}>
              <SelectTrigger data-testid="overline-font-select" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="mono">Moderna (máquina de escribir)</SelectItem><SelectItem value="display">Igual que los títulos</SelectItem><SelectItem value="body">Igual que los textos</SelectItem></SelectContent>
            </Select>
          </Field>
          <label className="flex items-center justify-between text-sm">En MAYÚSCULAS <Switch data-testid="overline-upper-switch" checked={th.overline_upper !== false} onCheckedChange={(v) => setT("overline_upper", v)} /></label>
          <p className="text-xs text-[#4A3525]">El texto se escribe en “Sitio web → Texto sobre la portada”.</p>
        </Card>
        <button data-testid="design-reset-button" onClick={() => setS({ ...s, theme: { ...DEFAULT_THEME, colors: { ...DEFAULT_THEME.colors } } })} className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#2C2016]/20 bg-white py-3 text-sm font-semibold"><RotateCcw className="h-4 w-4" />Restaurar diseño original</button>
        <button data-testid="design-save-button" onClick={save} className="w-full rounded-xl bg-[#B8532F] py-4 font-semibold text-white">Guardar diseño</button>
        <p className="text-xs text-[#4A3525]">Los cambios se previsualizan al instante; se publican al guardar.</p>
      </div>
    </div>
  );
};
